import graphql from "babel-plugin-relay/macro";
import { useCallback, useRef, useState } from "react";
import { useMutation } from "react-relay";

import { RestrictionFeature } from "../../types";
import {
  useValidateBookingRulesMutation,
  ValidateBookingRulesInput,
} from "./__generated__/useValidateBookingRulesMutation.graphql";

export type ValidateBookingRulesInputType = ValidateBookingRulesInput;

export const useValidateBookingRules = () => {
  const [validateBookingRules] = useMutation<useValidateBookingRulesMutation>(
    useValidateBookingRulesMutationString
  );
  const latestRequestIdRef = useRef(0);

  const [roundRestrictions, setRoundRestrictions] = useState<
    RestrictionFeature.RestrictionBarData[][]
  >([]);

  const [groupRestrictions, setGroupRestrictions] = useState<
    RestrictionFeature.RestrictionBarData[] | null
  >(null);

  const validateRestrictions = useCallback(
    (input: ValidateBookingRulesInput) => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      validateBookingRules({
        variables: { input },
        onCompleted: (res) => {
          if (requestId !== latestRequestIdRef.current) {
            return;
          }

          if (res?.validateBookingRules?.roundStatuses) {
            const newRoundRestrictions: RestrictionFeature.RestrictionBarData[][] =
              res.validateBookingRules.roundStatuses.map((rs) => {
                return (
                  rs?.statuses?.flatMap((status) => (status ? [status] : [])) ??
                  []
                );
              });
            setRoundRestrictions(newRoundRestrictions);
          }

          if (res.validateBookingRules?.groupStatuses?.statuses) {
            const newGroupRestrictions: RestrictionFeature.RestrictionBarData[] =
              res.validateBookingRules.groupStatuses.statuses.flatMap(
                (status) => (status ? [status] : [])
              );
            setGroupRestrictions(newGroupRestrictions);
          }
        },
        onError: (error) => console.log(error),
      });
    },
    [validateBookingRules]
  );

  return {
    roundRestrictions,
    groupRestrictions,
    validateRestrictions,
  } as const;
};

export const useValidateBookingRulesMutationString = graphql`
  mutation useValidateBookingRulesMutation($input: ValidateBookingRulesInput!) {
    validateBookingRules(input: $input) {
      groupStatuses {
        statuses {
          count
          willViolate
          rule {
            __typename
            id
            name
            ... on BookingRestrictionRule {
              bookingType
              displayName
              memberMessage
              memberOverride
              staffOverride
              visibilityColor
              countLimit {
                countLimit
                countUnit
                qualifier
                timeLimit
                timeUnit
                showNonViolating
              }
              bookingCriteria {
                minPlayers
                maxPlayers
              }
            }
          }
        }
      }
      roundStatuses {
        statuses {
          count
          willViolate
          rule {
            __typename
            id
            name
            ... on BookingRestrictionRule {
              bookingType
              displayName
              memberMessage
              memberOverride
              staffOverride
              countLimit {
                countLimit
                countUnit
                qualifier
                timeLimit
                timeUnit
                showNonViolating
              }
              bookingCriteria {
                minPlayers
                maxPlayers
              }
            }
          }
        }
      }
    }
  }
`;
